// Culling maths (architecture §9.2 step 4, §9.5): the world rectangle a camera shows on the
// canvas, and the bounding-box tests that skip off-screen entities. DOM-free and allocation-free.

/** A world-space rectangle (inclusive bounds). Structurally the same as game/camera.ts WorldRect. */
export interface ViewRect {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function makeViewRect(): ViewRect {
  return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
}

/**
 * The world rectangle visible on a `cssW × cssH` canvas whose centre shows world point
 * `(cx, cy)` at `zoom` CSS px per unit, grown by `marginPx` screen pixels on every side
 * (protocol §8.5: `world = (p − screenCentre) / zoom + cameraCentre`).
 */
export function viewWorldRect(
  cx: number,
  cy: number,
  zoom: number,
  cssW: number,
  cssH: number,
  marginPx = 0,
  out: ViewRect = makeViewRect(),
): ViewRect {
  const z = zoom > 0 && Number.isFinite(zoom) ? zoom : 1;
  const halfW = (cssW / 2 + marginPx) / z;
  const halfH = (cssH / 2 + marginPx) / z;
  out.minX = cx - halfW;
  out.maxX = cx + halfW;
  out.minY = cy - halfH;
  out.maxY = cy + halfH;
  return out;
}

/** True when the bounding box of the circle `(x, y, r)` meets `rect` (touching counts). */
export function circleInRect(x: number, y: number, r: number, rect: ViewRect): boolean {
  return x + r >= rect.minX && x - r <= rect.maxX && y + r >= rect.minY && y - r <= rect.maxY;
}

/** True when the two rectangles overlap (touching counts). */
export function rectsOverlap(a: ViewRect, b: ViewRect): boolean {
  return a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY;
}

/** Writes `a ∩ b` into `out`; false (out unspecified) when they do not overlap. */
export function intersectRects(a: ViewRect, b: ViewRect, out: ViewRect): boolean {
  out.minX = Math.max(a.minX, b.minX);
  out.minY = Math.max(a.minY, b.minY);
  out.maxX = Math.min(a.maxX, b.maxX);
  out.maxY = Math.min(a.maxY, b.maxY);
  return out.minX <= out.maxX && out.minY <= out.maxY;
}
